import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle2,
  ArrowRight,
  Trophy,
  BookOpen,
  Target,
  FileText,
  HelpCircle,
  MessageSquare,
  Award,
  Users,
  PartyPopper,
} from 'lucide-react';

interface NextModuleInfo {
  id: string;
  title: string;
  module_type: string;
  sequence_order: number;
  estimated_minutes?: number | null;
  simulation_agent_id?: string | null;
}

interface ModuleCompletionCardProps {
  completedModuleTitle: string;
  nextModule?: NextModuleInfo | null;
  pathwayId: string;
  modulesCompleted: number;
  totalModules: number;
  isPathwayComplete: boolean;
  score?: number;
  passed?: boolean;
}

function getModuleIcon(type: string) {
  switch (type) {
    case 'coaching': return BookOpen;
    case 'simulation': return Target;
    case 'practice': return Users;
    case 'debrief': return MessageSquare;
    case 'assessment': return Award;
    case 'reading': return FileText;
    case 'quiz': return HelpCircle;
    default: return BookOpen;
  }
}

function getModuleTypeLabel(type: string) {
  switch (type) {
    case 'coaching': return 'Coaching Session';
    case 'simulation': return 'Simulation';
    case 'practice': return 'Practice Session';
    case 'debrief': return 'Debrief Session';
    case 'assessment': return 'Assessment';
    case 'reading': return 'Reading';
    case 'quiz': return 'Quiz';
    default: return 'Module';
  }
}

function getModuleRoute(module: NextModuleInfo, pathwayId: string): string {
  const base = (() => {
    switch (module.module_type) {
      case 'reading':
        return `/learning/reading/${module.id}`;
      case 'quiz':
        return `/learning/quiz/${module.id}`;
      case 'coaching':
      case 'debrief':
        return module.simulation_agent_id
          ? `/coaching/${module.simulation_agent_id}/session`
          : `/learning/pathway/${pathwayId}`;
      case 'simulation':
      case 'practice':
      case 'assessment':
        return module.simulation_agent_id
          ? `/simulation/${module.simulation_agent_id}`
          : `/learning/pathway/${pathwayId}`;
      default:
        return `/learning/pathway/${pathwayId}`;
    }
  })();

  const separator = base.includes('?') ? '&' : '?';
  let url = `${base}${separator}pathway=${pathwayId}&module=${module.id}`;
  if (module.module_type === 'debrief') url += '&debrief=true';
  if (module.module_type === 'assessment') url += '&assessment=true';
  return url;
}

export function ModuleCompletionCard({
  completedModuleTitle,
  nextModule,
  pathwayId,
  modulesCompleted,
  totalModules,
  isPathwayComplete,
  score,
  passed,
}: ModuleCompletionCardProps) {
  const navigate = useNavigate();
  const progressPercent = totalModules > 0 ? Math.round((modulesCompleted / totalModules) * 100) : 0;

  if (isPathwayComplete) {
    return (
      <Card className="border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30">
        <CardContent className="pt-8 pb-6 text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center">
              <Trophy className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
              Pathway Complete!
            </h3>
            <p className="text-yellow-700 dark:text-yellow-300 mt-1">
              Congratulations! You've completed all {totalModules} modules.
            </p>
          </div>

          <div className="max-w-md mx-auto space-y-1">
            <div className="flex justify-between text-sm text-yellow-700 dark:text-yellow-300">
              <span>{modulesCompleted} of {totalModules} modules</span>
              <span>100%</span>
            </div>
            <Progress value={100} className="h-3" />
          </div>

          <Button
            onClick={() => navigate(`/learning/pathway/${pathwayId}`)}
            variant="outline"
            className="mt-4"
          >
            View Pathway Summary
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const NextIcon = nextModule ? getModuleIcon(nextModule.module_type) : BookOpen;

  return (
    <Card className="border-2 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
      <CardContent className="pt-8 pb-6 space-y-5">
        {/* Celebration Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-green-600" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-green-800 dark:text-green-200">
            Module Complete!
          </h3>
          <p className="text-green-700 dark:text-green-300 text-sm">
            You finished <strong>{completedModuleTitle}</strong>
            {score !== undefined && ` with a score of ${score}%`}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-md mx-auto space-y-1">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{modulesCompleted} of {totalModules} modules complete</span>
            <span>{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2.5" />
        </div>

        {/* Next Module Preview */}
        {nextModule && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border p-4 max-w-md mx-auto">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Up Next</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <NextIcon className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{nextModule.title}</p>
                <p className="text-xs text-muted-foreground">
                  {getModuleTypeLabel(nextModule.module_type)}
                  {nextModule.estimated_minutes && ` · ~${nextModule.estimated_minutes} min`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          {nextModule && (
            <Button
              onClick={() => navigate(getModuleRoute(nextModule, pathwayId))}
              className="flex-1 gap-2"
            >
              Continue to Next Module
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/learning/pathway/${pathwayId}`)}
            className={nextModule ? '' : 'flex-1'}
          >
            Back to Pathway
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
